import moment from 'moment-timezone';
import {
  IDAFMonthly as IMonthly,
  IDAFValueMonthly as IValueMonthly,
  IDAFValueU as IValueUnion,
  IDAFValueWeekly as IValueWeekly,
  IDAFWeekly as IWeekly,
  TDAFUnit as IUnit
} from 'volta/models/types/planning-settings';
import { getEveryYearWeek, getWeeksOfYearMonth } from 'volta/utils/date-utils';

import { action } from '@ember/object';
import Component from '@glimmer/component';

interface IArgs {
  unit: IUnit;
  bgHue?: string;
  heatmapMonthly?: IMonthly;
  heatmapWeekly?: IWeekly;
  toPreviousYearExceptions?: (year: number) => void;
  toNextYearExceptions?: (year: number) => void;
  onChange?: (value: IValueUnion) => void;
  year: number;
  defaultValue: number;
  style?: 'percent' | 'number';
  suffix?: string;
}

export default class DafCalendar extends Component<IArgs> {
  // Computed Properties
  // ~~~~~

  get bgHue() {
    return this.args.bgHue ?? 'var(--bv-color-teal-4-rgb)';
  }

  get isMonthly() {
    return this.args.unit === 'MONTHLY';
  }

  get currentPeriodDate(): Date {
    return moment
      .utc()
      .startOf(this.isMonthly ? 'month' : 'isoWeek')
      .toDate();
  }

  get currentYear() {
    return moment().year();
  }

  get months(): IValueMonthly[] {
    const year = this.args.year || new Date().getFullYear();

    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
      const date = moment
        .utc()
        .month(month - 1)
        .year(year)
        .date(1);

      const monthKey = `${month}` as keyof IMonthly;

      const heatmap = this.args.heatmapMonthly?.[monthKey];

      const hasNoWeekOverride = getWeeksOfYearMonth(year, date.month()).every((weekKey) => {
        const { heatmapWeekly } = this.args;

        if (heatmapWeekly && Object.keys(heatmapWeekly).length) {
          return heatmapWeekly[`${weekKey}` as keyof IWeekly] === heatmap;
        }
        return true;
      });

      const compared = heatmap && Math.abs(1 / (this.args.defaultValue / heatmap));

      return {
        unit: 'MONTHLY',
        period: monthKey,
        date: date.toDate(),
        heatmap,
        compared,
        isOverriden: !hasNoWeekOverride,
        isPast: date.isBefore(this.currentPeriodDate),
        isCurrent: date.isSame(this.currentPeriodDate)
      };
    });
  }

  get weeks(): IValueWeekly[] {
    const year = this.args.year || new Date().getFullYear();

    return getEveryYearWeek(year).map((weekDate) => {
      const mDate = moment(weekDate);
      const weekKey = `${mDate.isoWeek()}` as keyof IWeekly;

      const heatmap = this.args.heatmapWeekly?.[weekKey];

      const compared = heatmap && Math.abs(1 / (this.args.defaultValue / heatmap));
      return {
        unit: 'WEEKLY' as IUnit,
        period: weekKey,
        date: mDate.toDate(),
        heatmap,
        compared,
        isPast: mDate.isBefore(this.currentPeriodDate),
        isCurrent: mDate.isSame(this.currentPeriodDate)
      };
    });
  }

  get weeksByMonths() {
    const { months, weeks } = this;

    return months.reduce((acc: Record<string, IValueWeekly[]>, month) => {
      const period = +month.period - 1;
      const monthWeeks = getWeeksOfYearMonth(
        this.args.year ?? new Date().getFullYear(),
        period
      ) as number[];
      const weeksOfMonth = weeks.filter((week) => monthWeeks.includes(+week.period));

      acc[moment(month.date).format('MMM')] = weeksOfMonth;
      return acc;
    }, {});
  }

  get periods() {
    return this.isMonthly ? this.months : this.weeks;
  }

  // Actions
  // ~~~~~

  @action
  onSave(periodObject: IValueUnion, value: number) {
    this.args.onChange?.({ ...periodObject, value });
  }
}
